/**
 * useLinkContent Hook
 *
 * Loads paginated linkable medias for the current user.
 * Mirrors Flutter's LinkContentScreen fetch flow (link_content_screen.dart).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PageCursor } from '@/data/types/json';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import {
  FetchLinkableMediasUseCase,
  createFetchLinkableMediasUseCase,
} from '@/domain/usecases/userMedia/fetchLinkableMediasUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

interface UseLinkContentParams {
  excludeMediaId: string;
}

interface UseLinkContentResult {
  medias: UserMediaEntity[];
  hasMore: boolean;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

export const useLinkContent = ({
  excludeMediaId,
}: UseLinkContentParams): UseLinkContentResult => {
  const useCaseRef = useRef<FetchLinkableMediasUseCase | null>(null);
  if (!useCaseRef.current) {
    useCaseRef.current = createFetchLinkableMediasUseCase();
  }
  const lastFetchedItemRef = useRef<PageCursor | undefined>(
    undefined,
  );
  const inFlightRef = useRef(false);

  const [medias, setMedias] = useState<UserMediaEntity[]>([]);
  // `false` until the first fetch reports real capacity — prevents a scroll
  // firing during the initial render from triggering a load-more path.
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPage = useCallback(
    async (isInitial: boolean) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      if (!isInitial) setIsLoadingMore(true);

      try {
        const result = await useCaseRef.current!.execute(
          excludeMediaId,
          lastFetchedItemRef.current,
        );
        lastFetchedItemRef.current = result.cursor;
        setMedias((prev) =>
          isInitial ? result.medias : [...prev, ...result.medias],
        );
        setHasMore(result.hasMore);
        setError(null);
      } catch (err) {
        appLogger.error(
          'Failed to fetch linkable medias',
          'useLinkContent.fetchPage', { error: err as Error },
        );
        setError(err as Error);
        // Keep hasMore as-is so a subsequent scroll can retry the fetch.
      } finally {
        inFlightRef.current = false;
        if (isInitial) setIsInitialLoading(false);
        else setIsLoadingMore(false);
      }
    },
    [excludeMediaId],
  );

  useEffect(() => {
    fetchPage(true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return;
    fetchPage(false);
  }, [fetchPage, hasMore]);

  return { medias, hasMore, isInitialLoading, isLoadingMore, error, loadMore };
};
